import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LoginForm } from './components/auth/LoginForm';
import { EmailVerificationScreen } from './components/auth/EmailVerificationScreen';
import { ProfileOnboarding } from './components/auth/ProfileOnboarding';
import { Sidebar } from './components/layout/Sidebar';
import { LogosModule } from './modules/logos/LogosModule';
import { ProjectNamesModule } from './modules/project-names/ProjectNamesModule';
import { PeopleModule } from './modules/people/PeopleModule';
import { SuggestionBoxModule } from './modules/suggestion-box/SuggestionBoxModule';
import { BugReportModal } from './modules/bug-report/BugReportModule';
import { ProfilePage } from './components/profile/ProfilePage';
import { ServicesModule } from './modules/services/ServicesModule';
import { DomainsModule } from './modules/domains/DomainsModule';
import { AdminUserManagement } from './components/admin/AdminUserManagement';
import { AdminWhitelistManagement } from './components/admin/AdminWhitelistManagement';
import { AdminTagManagement } from './components/admin/AdminTagManagement';
import { AdminSkillsManagement } from './components/admin/AdminSkillsManagement';
import { peopleService } from './modules/people/peopleService';
import { Person } from './types';
import { signOut } from 'firebase/auth';
import { auth } from './lib/firebase';
import { FirebaseImage } from './components/shared/FirebaseImage';

function AppContent() {
  const { user, loading, needsEmailVerification, appUser } = useAuth();
  const [activeModule, setActiveModule] = useState('home');
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [userPerson, setUserPerson] = useState<Person | null>(null);
  const [showBugReport, setShowBugReport] = useState(false);

  // Check if user needs to complete profile setup and load person data
  useEffect(() => {
    const checkProfileSetup = async () => {
      if (!appUser?.email) {
        setCheckingProfile(false);
        return;
      }

      try {
        const existingPerson = await peopleService.getPersonByEmail(appUser.email);
        setUserPerson(existingPerson);
        
        // Only show onboarding if NO person record exists
        // Once completed, never show again
        setNeedsProfileSetup(!existingPerson);
        
      } catch (error) {
        console.error('Error checking profile setup:', error);
        setNeedsProfileSetup(true); // Assume needs setup if error
      } finally {
        setCheckingProfile(false);
      }
    };

    if (appUser && !needsEmailVerification) {
      checkProfileSetup();
    } else {
      setCheckingProfile(false);
    }
  }, [appUser, needsEmailVerification]);

  // Generate initials for avatar
  const getInitials = (name: string | undefined): string => {
    if (!name) return appUser?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?';
    return name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    signOut(auth);
  };

  const refreshUserPerson = async () => {
    if (appUser?.email) {
      try {
        const updatedPerson = await peopleService.getPersonByEmail(appUser.email);
        setUserPerson(updatedPerson);
      } catch (error) {
        console.error('Error refreshing user person data:', error);
      }
    }
  };

  const handleProfileComplete = async () => {
    setNeedsProfileSetup(false);
    // Reload the user person data to show profile picture in header
    if (appUser?.email) {
      try {
        const updatedPerson = await peopleService.getPersonByEmail(appUser.email);
        setUserPerson(updatedPerson);
      } catch (error) {
        console.error('Error reloading person data:', error);
      }
    }
  };

  const handleBugReportClick = () => {
    setShowBugReport(true);
  };

  if (loading || checkingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm onSuccess={() => {}} />;
  }

  // Show email verification screen if needed
  if (needsEmailVerification) {
    return <EmailVerificationScreen />;
  }

  // Show profile onboarding if needed
  if (needsProfileSetup) {
    return <ProfileOnboarding onComplete={handleProfileComplete} />;
  }

  const renderModule = () => {
    switch (activeModule) {
      case 'home':
        return (
          <div className="bg-white p-8 rounded-lg border shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Welcome to Gule Sider</h2>
            <p className="text-muted-foreground">
              Your central hub for managing creative assets. Select a module from the sidebar to get started.
            </p>
          </div>
        );
      case 'people':
        return <PeopleModule />;
      case 'suggestion-box':
        return <SuggestionBoxModule />;
      case 'profile':
        return <ProfilePage onProfileUpdate={refreshUserPerson} />;
      case 'admin-users':
        return <AdminUserManagement />;
      case 'admin-tags':
        return <AdminTagManagement />;
      case 'admin-skills':
        return <AdminSkillsManagement />;
      case 'admin-whitelist':
        return <AdminWhitelistManagement />;
      case 'logos':
        return <LogosModule />;
      case 'project-names':
        return <ProjectNamesModule />;
      case 'services':
        return <ServicesModule />;
      case 'domains':
        return <DomainsModule />;
      default:
        return (
          <div className="bg-white p-8 rounded-lg border shadow-sm">
            <h2 className="text-xl font-semibold mb-4">
              {activeModule.charAt(0).toUpperCase() + activeModule.slice(1).replace('-', ' ')} Module
            </h2>
            <p className="text-muted-foreground">
              This module is coming soon!
            </p>
          </div>
        );
    }
  };

  const getModuleTitle = () => {
    switch (activeModule) {
      case 'home':
        return 'Dashboard';
      case 'profile':
        return 'My Profile';
      case 'people':
        return 'People';
      case 'suggestion-box':
        return 'Suggestion Box';
      case 'admin-users':
        return 'User Management';
      case 'admin-tags':
        return 'Tag Management';
      case 'admin-skills':
        return 'Skills Management';
      case 'admin-whitelist':
        return 'Whitelist Management';
      case 'logos':
        return 'Logos';
      case 'project-names':
        return 'Project Names';
      case 'services':
        return 'Services & Subscriptions';
      case 'domains':
        return 'Domains';
      case 'color-palettes':
        return 'Color Palettes';
      case 'print-templates':
        return 'Print Templates';
      case 'digital-templates':
        return 'Digital Templates';
      case 'texts':
        return 'Texts';
      case 'wiki':
        return 'Wiki';
      case 'prompts':
        return 'AI Prompts';
      default:
        return activeModule.charAt(0).toUpperCase() + activeModule.slice(1).replace('-', ' ');
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar 
        activeModule={activeModule} 
        onModuleChange={setActiveModule}
        onBugReportClick={handleBugReportClick}
      />
      
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-card border-b p-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-foreground">
            {getModuleTitle()}
          </h1>
          <div className="flex items-center gap-4">
            {/* User Profile Link */}
            <button
              onClick={() => setActiveModule('profile')}
              className="flex items-center gap-3 hover:bg-gray-100 rounded-lg p-2 transition-colors group"
            >
              {/* User Avatar */}
              <div className="w-8 h-8 rounded-full border-2 border-gray-200 overflow-hidden">
                <FirebaseImage
                  src={userPerson?.profilePictureUrl}
                  storagePath={userPerson?.profilePicturePath}
                  alt={userPerson?.name || appUser?.displayName || 'User'}
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {getInitials(userPerson?.name)}
                    </div>
                  }
                />
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 group-hover:text-primary">
                  {userPerson?.name || appUser?.displayName || 'User'}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.email}
                </div>
              </div>
            </button>
            
            <div className="h-6 border-l border-gray-300"></div>
            
            <button
              onClick={handleLogout}
              className="bg-secondary text-secondary-foreground px-4 py-2 rounded hover:bg-secondary/80 transition-colors"
            >
              Logout
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {renderModule()}
        </main>
      </div>

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={showBugReport}
        onClose={() => setShowBugReport(false)}
        currentModule={activeModule}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;